/**
 * Classifies a failed `/auth/v1/token?grant_type=refresh_token` response.
 *
 * "Invalid Refresh Token: Already Used" is BENIGN when it is a race — two
 * parallel refreshes, the other one won and the session already holds the new
 * token. It is TERMINAL when it repeats: the stored refresh token is dead
 * (rotated away by another client sharing the cookie, or its family revoked)
 * and every further attempt will 400 forever. The old code classified the
 * message as benign unconditionally, so a dead token produced a tight loop —
 * ~190 identical 400s in ~35 s, no backoff, no "session expired" surface, and
 * the Conference silently degraded to a signed-out view (extreme module 08
 * defect 14, staging 2026-08-17). After ALREADY_USED_BENIGN_MAX occurrences
 * inside ALREADY_USED_WINDOW_MS the same message counts as terminal, which
 * lets the existing circuit breaker clear the dead auth state and raise the
 * ReauthModal.
 */
export const ALREADY_USED_BENIGN_MAX = 3;
export const ALREADY_USED_WINDOW_MS = 30_000;

export type RefreshFailureKind = 'benign_race' | 'terminal' | 'other';

export interface AlreadyUsedTracker {
  count: number;
  windowStart: number;
}

export function createAlreadyUsedTracker(): AlreadyUsedTracker {
  return { count: 0, windowStart: 0 };
}

export function isAlreadyUsedMessage(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('refresh token') && m.includes('already used');
}

/**
 * Records one "already used" observation and says whether it is still within
 * the benign-race allowance. Mutates the tracker.
 */
export function classifyAlreadyUsed(tracker: AlreadyUsedTracker, now: number): RefreshFailureKind {
  if (now - tracker.windowStart > ALREADY_USED_WINDOW_MS) {
    tracker.count = 0;
    tracker.windowStart = now;
  }
  tracker.count += 1;
  return tracker.count <= ALREADY_USED_BENIGN_MAX ? 'benign_race' : 'terminal';
}
